import { AlertTriangle, Bot, Rocket, Wrench, type LucideIcon } from 'lucide-react';
import type { AgentActionStats } from '../types';

interface StatsCardsProps {
  stats?: AgentActionStats;
  loading?: boolean;
}

type StatCard = {
  label: string;
  value: number;
  Icon: LucideIcon;
  circleClass: string;
  iconClass: string;
  cardBorder: string;
  valueClass: string;
};

export default function StatsCards({ stats, loading }: StatsCardsProps) {
  const s = stats || { agent_runs: 0, fixes: 0, deploys: 0, failed: 0 };

  const cards: StatCard[] = [
    {
      label: 'Agent runs',
      value: s.agent_runs,
      Icon: Bot,
      circleClass: 'bg-blue-100 dark:bg-blue-900/40 ring-2 ring-blue-200/80 dark:ring-blue-800',
      iconClass: 'text-blue-700 dark:text-blue-300',
      cardBorder: 'border-blue-200/80 dark:border-blue-800',
      valueClass: 'text-gray-900 dark:text-gray-100',
    },
    {
      label: 'Fixes applied',
      value: s.fixes,
      Icon: Wrench,
      circleClass: 'bg-green-100 dark:bg-green-900/40 ring-2 ring-green-200/80 dark:ring-green-800',
      iconClass: 'text-green-700 dark:text-green-300',
      cardBorder: 'border-green-200/80 dark:border-green-800',
      valueClass: 'text-gray-900 dark:text-gray-100',
    },
    {
      label: 'Deploys',
      value: s.deploys,
      Icon: Rocket,
      circleClass: 'bg-teal-100 dark:bg-teal-900/40 ring-2 ring-teal-200/80 dark:ring-teal-800',
      iconClass: 'text-teal-700 dark:text-teal-300',
      cardBorder: 'border-teal-200/80 dark:border-teal-800',
      valueClass: 'text-gray-900 dark:text-gray-100',
    },
    {
      label: 'Failed',
      value: s.failed,
      Icon: AlertTriangle,
      circleClass: 'bg-red-100 dark:bg-red-900/40 ring-2 ring-red-200/80 dark:ring-red-800',
      iconClass: 'text-red-700 dark:text-red-300',
      cardBorder: 'border-red-200/80 dark:border-red-800',
      valueClass: s.failed > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      {cards.map((c) => (
        <div
          key={c.label}
          className={`flex items-center gap-3 rounded-lg border p-3.5 bg-white dark:bg-gray-900 shadow-sm ${c.cardBorder}`}
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${c.circleClass}`}>
            <c.Icon className={`w-5 h-5 ${c.iconClass}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 dark:text-gray-400 m-0 mb-0.5">{c.label}</p>
            <p className={`text-2xl font-semibold m-0 text-right tabular-nums ${c.valueClass}`}>
              {loading ? '…' : c.value}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
